import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'

const DOMAIN = 'dagger'

export const SCHEDULER_KEYS = {
  schedules: ['managed-schedules'],
  schedule: (id) => ['managed-schedule', id],
  unmanagedSchedules: ['unmanaged-schedules'],
  unmanagedSchedule: (name) => ['unmanaged-schedule', name],
  targets: ['schedule-targets'],
  targetsAdmin: ['schedule-targets', 'admin'],
  invocations: (id) => ['schedule-invocations', id],
  allInvocations: ['all-invocations'],
  changes: (id) => ['schedule-changes', id],
  preview: (expr, tz) => ['schedule-preview', expr, tz]
}

export const useListManagedSchedules = () =>
  useQuery({
    queryKey: SCHEDULER_KEYS.schedules,
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-managed-schedules', {})
      return data
    },
    staleTime: 30_000
  })

export const useListUnmanagedSchedules = () =>
  useQuery({
    queryKey: SCHEDULER_KEYS.unmanagedSchedules,
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-unmanaged-schedules', {})
      return data
    },
    staleTime: 30_000
  })

export const useGetUnmanagedSchedule = (name) =>
  useQuery({
    queryKey: SCHEDULER_KEYS.unmanagedSchedule(name),
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'get-unmanaged-schedule', { name })
      return data
    },
    enabled: !!name
  })

export const useGetManagedSchedule = (scheduleId, options = {}) =>
  useQuery({
    queryKey: SCHEDULER_KEYS.schedule(scheduleId),
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'get-managed-schedule', { scheduleId })
      return data
    },
    enabled: !!scheduleId,
    ...options
  })

export const useListScheduleTargets = () =>
  useQuery({
    queryKey: SCHEDULER_KEYS.targets,
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-schedule-targets', {})
      return data
    },
    staleTime: 30_000
  })

export const useListScheduleTargetsAdmin = () =>
  useQuery({
    queryKey: SCHEDULER_KEYS.targetsAdmin,
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-schedule-targets', { includeDisabled: true })
      return data
    },
    staleTime: 30_000
  })

export const useListScheduleInvocations = (scheduleId, { page = 1, pageSize = 25 } = {}) =>
  useQuery({
    queryKey: [...SCHEDULER_KEYS.invocations(scheduleId), page, pageSize],
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-schedule-invocations', { scheduleId, page, pageSize })
      return data
    },
    enabled: !!scheduleId
  })

export const useListAllInvocations = ({ page = 1, pageSize = 25, status } = {}) =>
  useQuery({
    queryKey: [...SCHEDULER_KEYS.allInvocations, page, pageSize, status],
    queryFn: async () => {
      const params = { page, pageSize }
      if (status) params.status = status
      const { data } = await postNamedQuery(DOMAIN, 'list-all-invocations', params)
      return data
    }
  })

export const useListScheduleChanges = (scheduleId, { page = 1, pageSize = 25 } = {}) =>
  useQuery({
    queryKey: [...SCHEDULER_KEYS.changes(scheduleId), page, pageSize],
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-schedule-changes', { scheduleId, page, pageSize })
      return data
    },
    enabled: !!scheduleId
  })

export const usePreviewManagedSchedule = (expression, timezone) =>
  useQuery({
    queryKey: SCHEDULER_KEYS.preview(expression, timezone),
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'preview-managed-schedule', { expression, timezone })
      return data
    },
    enabled: !!expression,
    staleTime: 2000,
    keepPreviousData: true
  })

export const useCreateManagedSchedule = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'create-managed-schedule', body)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.schedules }).catch(console.error)
    }
  })
}

export const useModifyManagedSchedule = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'modify-managed-schedule', body)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.schedules }).catch(console.error)
      if (variables.scheduleId) {
        queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.schedule(variables.scheduleId) }).catch(console.error)
      }
    }
  })
}

export const useRemoveManagedSchedule = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'remove-managed-schedule', body)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.schedules }).catch(console.error)
    }
  })
}

export const useEnableManagedSchedule = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'enable-managed-schedule', body)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.schedules }).catch(console.error)
      if (variables.scheduleId) {
        queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.schedule(variables.scheduleId) }).catch(console.error)
      }
    }
  })
}

export const useDisableManagedSchedule = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'disable-managed-schedule', body)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.schedules }).catch(console.error)
      if (variables.scheduleId) {
        queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.schedule(variables.scheduleId) }).catch(console.error)
      }
    }
  })
}

export const useScanLambdaTargets = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await postNamedCommand(DOMAIN, 'scan-lambda-targets', {})
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.targets }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.targetsAdmin }).catch(console.error)
    }
  })
}
